/* eslint-disable camelcase */

import { ArchiveDeleteResponse } from "components/common/ArchiveDialog/ArchiveDialog";
import { SWRConfiguration } from "swr";

import { ApiArrayResponse, ApiResponse, ServerResponse } from "./axiosConfig";
import { useSwr, axiosInst } from "./axiosWIS";
import { Attribute, EntityLocation } from "./hvsBase";
import { IconData, IncidentType } from "./incidentType";
import { Priority } from "./priority";
import { Severity } from "./severity";
import { Status } from "./status";

export interface AssignedUser {
  id: string;
  email: string;
  name: string;
  surname: string;
}

export interface IncidentSop extends Record<string, unknown> {
  id: string;
  sop_completed_steps: number;
  sop_name: string;
  sop_desc: string;
  sop_initiated_date: string;
  sop_steps: SopStep[];
}

export interface CorrelatedEntity {
  entity_id: string;
  entity_type_id: string;

  create_timestamp: string;
  update_timestamp: string;
}

export interface Entity extends Record<string, unknown> {
  id: string;

  type_id: string;

  name: string;
  description?: string;

  address: string;
  location: EntityLocation;
  geohash: string;

  appearance?: IconData;

  distance_from_location: number;
}

export interface Camera extends Entity {
  distance_from_incident_location: number;
  attributes: Record<string, unknown>;
}

export interface Incident {
  status: Status;
  severity: Severity;
  priority: Priority;
  incident_type: IncidentType;
  id: string;

  pk_inc_id: number;
  inc_name: string;
  inc_desc: string;
  fk_inc_type_id: number;
  fk_inc_priority_id: number;
  fk_inc_severity_id: number;
  fk_inc_status_id: number;

  appearance: IconData;

  inc_source: string | undefined;
  inc_assigned_user: User;
  inc_watchlist: User[];

  hv_entity_ids: string[];
  hv_entity_type_id: string;
  inc_nearest_camera_ids: string[];
  inc_nearest_cameras: Camera[];
  inc_sop_map: IncidentSop[];
  incident_count: number;
  inc_status: string;
  inc_status_type: string;

  hv_entitys: Entity[];
  hv_correlated_entities: CorrelatedEntity[];

  inc_overall_completed_steps: number;
  inc_overall_progress: number;
  inc_overall_steps: number;

  attributes: Record<string, Attribute>;
  inc_address?: string;
  location?: EntityLocation;
  nearest_cameras_dist_in_miles?: number;

  created_by: string;
  create_timestamp: string;
  update_by: string;
  update_timestamp: string;

  version: number;
}

export interface IncidentTransaction {
  id: string;
  fk_sop_template_id: string;
  fk_sop_step_id: string;
  fk_inc_id: number;
  inct_status: "InProgress" | "Completed";
  inct_status_value: "InProgress" | "Completed";
  inct_assigned_to: User;
  inct_value: string;
  inct_start_date_time: string;
  inct_completed_date_time: string;
  inc_trn_version: number;
  create_timestamp: string;
  created_by: string;
  update_timestamp: string;
  update_by: string;
}

interface CoordinatesLocation {
  lat: number;
  lng: number;
}

export interface MapBound {
  northEast: CoordinatesLocation;
  southWest: CoordinatesLocation;
}

export type KpiNames =
  | ""
  | "KPI_OPEN_INCIDENT"
  | "KPI_TODAY_INCIDENT"
  | "KPI_UNASSIGNED_INCIDENT"
  | "KPI_OVERDUE_INCIDENT";

export interface KPI {
  kpi_name: string;
  incident_ids: string[];
  count: number;
}

const baseKey = "/api/Incidents";

export const useIncidents = (
  params: {
    pageNr?: number;
    pageSize?: number;
    orderBy?: string;
    Order?: string;
    dateFrom?: string;
    dateTo?: string;
    SearchKey?: string;
  } = {
    pageNr: 1,
    pageSize: 200,
    orderBy: undefined,
    Order: "desc",
    dateFrom: undefined,
    dateTo: undefined,
    SearchKey: undefined,
  },
  config?: SWRConfiguration,
  requestBody?: Record<string, unknown>
): ServerResponse<ApiArrayResponse<Incident>> =>
  useSwr<ApiArrayResponse<Incident>>(
    `${baseKey}?pageNr=${params.pageNr}&pageSize=${params.pageSize}${
      params.orderBy != null
        ? `&orderBy=${params.orderBy}&Order=${params.Order}`
        : ""
    }${params.dateFrom != null ? `&dateFrom=${params.dateFrom}` : ""}${
      params.dateTo != null ? `&dateTo=${params.dateTo}` : ""
    }
    ${params.SearchKey != null ? `&SearchKey=${params.SearchKey}` : ""}`,
    config,
    requestBody
  );

export const useIncident = (
  incidentId?: string,
  dontExcludeEntities = false,
  config?: SWRConfiguration
): ServerResponse<ApiResponse<Incident>> => {
  const params = [];
  if (dontExcludeEntities) {
    params.push("isExcludeEntities=false");
  }
  return useSwr<ApiResponse<Incident>>(
    `${baseKey}/${incidentId}${
      params.length > 0 ? `?${params.join("&")}` : ""
    }`,
    config
  );
};

export const useIncidentTransactions = (
  incidentId?: string,
  config?: SWRConfiguration
): ServerResponse<ApiResponse<IncidentTransaction[]>> =>
  useSwr<ApiResponse<IncidentTransaction[]>>(
    `${baseKey}/${incidentId}/incidenttransactions`,
    config
  );

export const startWorkOnSopStep = async (
  incidentId: number,
  sopTemplateId: string,
  sopStepId: string
): Promise<ApiResponse<IncidentTransaction>> => {
  const res = await axiosInst.post(
    `/api/IncidentTransaction/incident/${incidentId}/soptemplateId/${sopTemplateId}/Stepid/${sopStepId}`
  );
  return res.data;
};

export const stopWorkOnSopStep = async (
  incidentId: number,
  sopTemplateId: string,
  sopStepId: string
): Promise<ApiResponse<unknown>> => {
  const res = await axiosInst.patch(
    `/api/IncidentTransaction/incident/${incidentId}/soptemplateId/${sopTemplateId}/Stepid/${sopStepId}/unassign`
  );
  return res.data;
};

export const saveComentOnSopStep = async (
  incidentId: number,
  sopTemplateId: string,
  sopStepId: string,
  comments: string
): Promise<ApiResponse<IncidentTransaction>> => {
  const res = await axiosInst.patch(
    `/api/IncidentTransaction/incident/${incidentId}/soptemplateId/${sopTemplateId}/Stepid/${sopStepId}/savedraft`,
    { comments }
  );
  return res.data;
};

export const completeSopStep = async (
  incidentId: number,
  sopTemplateId: string,
  sopStepId: string,
  comments: string
): Promise<ApiResponse<IncidentTransaction>> => {
  const res = await axiosInst.patch(
    `/api/IncidentTransaction/incident/${incidentId}/soptemplateId/${sopTemplateId}/Stepid/${sopStepId}/complete`,
    { comments }
  );
  return res.data;
};

export const addIncident = async (
  incidentTypeId: number,
  data: Partial<Incident>
): Promise<unknown> => {
  const res = await axiosInst.post(`${baseKey}/${incidentTypeId}`, data);
  return res.data;
};

export const editIncident = async (
  data: Partial<Incident>
): Promise<ApiResponse<Incident>> => {
  const res = await axiosInst.put(`${baseKey}/${data.id}`, data);
  return res.data;
};

export const archiveIncident = async (
  incidentId: string
): Promise<ArchiveDeleteResponse<number>> => {
  const res = await axiosInst.delete(`${baseKey}/${incidentId}`);
  return res.data;
};

export const useKpis = (
  params: {
    mapBound: MapBound;
    typeIds: string[];
  },
  config?: SWRConfiguration
): ServerResponse<ApiArrayResponse<KPI>> =>
  useSwr<ApiArrayResponse<KPI>>(
    `${baseKey}/kpi?lowerLeftX=${params?.mapBound?.southWest?.lng}&lowerLeftY=${params?.mapBound?.southWest?.lat}&upperRightX=${params?.mapBound?.northEast?.lng}&upperRightY=${params?.mapBound?.northEast?.lat}`,
    config,
    params.typeIds
  );
